/**
 * useCompliance Hooks - TASK-228
 * Custom hooks for accessing compliance data from ComplianceContext
 *
 * Provides optimized selectors for specific data slices,
 * reducing unnecessary re-renders.
 */

import { useMemo } from 'react';
import { useComplianceContext } from '../contexts/ComplianceContext';
import type {
  TrackedField,
  FormType,
  FormStatus,
  ScreeningStatus,
  RiskRating,
  DataSource,
  ClientComplianceData,
} from '../types/clientCompliance';

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Main hook to access the full ComplianceContext
 * Use more specific hooks below for better performance when only needing partial data
 */
export function useCompliance() {
  return useComplianceContext();
}

// ============================================================================
// FIELD SELECTORS
// ============================================================================

/**
 * Hook to select a specific tracked field from the compliance data
 * Memoized to prevent unnecessary re-renders
 *
 * @param section - The section containing the field (e.g., 'personal', 'contact')
 * @param field - The field name (e.g., 'firstName', 'emailPrimary')
 * @returns The TrackedField or undefined if not found
 *
 * @example
 * const firstName = useComplianceField<string>('personal', 'firstName');
 * console.log(firstName?.value, firstName?.confidence);
 */
export function useComplianceField<T>(
  section: string,
  field: string
): TrackedField<T> | undefined {
  const { data } = useComplianceContext();

  return useMemo(() => {
    if (!data) return undefined;

    const sectionData = data[section as keyof ClientComplianceData];
    if (typeof sectionData === 'object' && sectionData !== null) {
      return (sectionData as Record<string, TrackedField<T>>)[field];
    }
    return undefined;
  }, [data, section, field]);
}

/**
 * Hook to get the raw value of a tracked field (unwrapped)
 *
 * @param section - The section containing the field
 * @param field - The field name
 * @returns The raw value or undefined
 *
 * @example
 * const firstName = useComplianceFieldValue<string>('personal', 'firstName');
 * // firstName is string | undefined, not TrackedField<string>
 */
export function useComplianceFieldValue<T>(
  section: string,
  field: string
): T | undefined {
  const trackedField = useComplianceField<T>(section, field);
  return useMemo(() => trackedField?.value, [trackedField]);
}

/**
 * Hook to get the confidence score of a tracked field
 *
 * @param section - The section containing the field
 * @param field - The field name
 * @returns The confidence score (0-1) or undefined
 */
export function useComplianceFieldConfidence(
  section: string,
  field: string
): number | undefined {
  const trackedField = useComplianceField<unknown>(section, field);
  return useMemo(() => trackedField?.confidence, [trackedField]);
}

// ============================================================================
// FORM STATUS HOOKS
// ============================================================================

/**
 * Hook to get the status of a specific form
 *
 * @param formType - The form type ('kyc', 'riskProfile', 'clientProfile', 'formA')
 * @returns The FormStatus or undefined if form not started
 *
 * @example
 * const kycStatus = useFormStatus('kyc');
 * console.log(kycStatus?.status, kycStatus?.completedSections);
 */
export function useFormStatus(formType: FormType): FormStatus | undefined {
  const { data } = useComplianceContext();

  return useMemo(() => {
    if (!data?.forms) return undefined;
    return data.forms[formType];
  }, [data?.forms, formType]);
}

/**
 * Hook to get all form statuses at once
 *
 * @returns Record of all form statuses
 */
export function useAllFormStatuses(): Record<FormType, FormStatus | undefined> {
  const { data } = useComplianceContext();

  return useMemo(() => {
    if (!data?.forms) {
      return {
        kyc: undefined,
        riskProfile: undefined,
        clientProfile: undefined,
        formA: undefined,
      };
    }
    return {
      kyc: data.forms.kyc,
      riskProfile: data.forms.riskProfile,
      clientProfile: data.forms.clientProfile,
      formA: data.forms.formA,
    };
  }, [data?.forms]);
}

// ============================================================================
// SCREENING HOOKS
// ============================================================================

/**
 * Hook to get all screening statuses
 *
 * @returns Array of screening statuses (PEP, Sanctions, Adverse Media)
 *
 * @example
 * const screenings = useScreeningStatus();
 * screenings.forEach(s => console.log(s.type, s.status));
 */
export function useScreeningStatus(): ScreeningStatus[] {
  const { data } = useComplianceContext();

  return useMemo(() => {
    if (!data?.screening) return [];

    const statuses: ScreeningStatus[] = [];
    if (data.screening.pep) statuses.push(data.screening.pep);
    if (data.screening.sanctions) statuses.push(data.screening.sanctions);
    if (data.screening.adverseMedia) statuses.push(data.screening.adverseMedia);

    return statuses;
  }, [data?.screening]);
}

/**
 * Hook to get a specific screening status
 *
 * @param type - The screening type ('pep', 'sanctions', 'adverseMedia')
 * @returns The ScreeningStatus or undefined
 */
export function useScreeningStatusByType(
  type: 'pep' | 'sanctions' | 'adverseMedia'
): ScreeningStatus | undefined {
  const { data } = useComplianceContext();

  return useMemo(() => {
    if (!data?.screening) return undefined;
    return data.screening[type];
  }, [data?.screening, type]);
}

/**
 * Hook to check if any screening has hits requiring review
 *
 * @returns true if any screening has status 'hit' or 'review_required'
 */
export function useHasScreeningHits(): boolean {
  const screenings = useScreeningStatus();

  return useMemo(() => {
    return screenings.some(
      (s) => s.status === 'hit' || s.status === 'review_required'
    );
  }, [screenings]);
}

// ============================================================================
// RISK RATING HOOKS
// ============================================================================

/**
 * Hook to get the current risk rating
 *
 * @returns The RiskRating or undefined
 *
 * @example
 * const risk = useRiskRating();
 * console.log(risk?.overall, risk?.score);
 */
export function useRiskRating(): RiskRating | undefined {
  const { data } = useComplianceContext();
  return useMemo(() => data?.riskRating, [data?.riskRating]);
}

/**
 * Hook to get just the overall risk level
 *
 * @returns The risk level ('low', 'medium', 'high', 'critical') or undefined
 */
export function useRiskLevel(): 'low' | 'medium' | 'high' | 'critical' | undefined {
  const riskRating = useRiskRating();
  return useMemo(() => riskRating?.overall, [riskRating]);
}

/**
 * Hook to check if the client is high risk
 *
 * @returns true if risk level is 'high' or 'critical'
 */
export function useIsHighRisk(): boolean {
  const riskLevel = useRiskLevel();
  return useMemo(
    () => riskLevel === 'high' || riskLevel === 'critical',
    [riskLevel]
  );
}

// ============================================================================
// FIELD HISTORY & AUDIT HOOKS
// ============================================================================

/**
 * Hook to get the history (audit trail) for a specific field
 *
 * @param section - The section containing the field
 * @param field - The field name
 * @returns Array of previous DataSource entries
 *
 * @example
 * const history = useFieldHistory('personal', 'firstName');
 * history.forEach(h => console.log(h.type, h.extractedAt));
 */
export function useFieldHistory(section: string, field: string): DataSource[] {
  const { getFieldHistory } = useComplianceContext();

  return useMemo(
    () => getFieldHistory(section, field),
    [getFieldHistory, section, field]
  );
}

/**
 * Hook to check if a field has conflicts (multiple different values from sources)
 *
 * @param section - The section containing the field
 * @param field - The field name
 * @returns true if field has conflicting values in history
 */
export function useFieldConflict(section: string, field: string): boolean {
  const { hasConflicts } = useComplianceContext();

  return useMemo(
    () => hasConflicts(section, field),
    [hasConflicts, section, field]
  );
}

/**
 * Hook to check if a field has been verified
 *
 * @param section - The section containing the field
 * @param field - The field name
 * @returns true if field is verified
 */
export function useFieldVerified(section: string, field: string): boolean {
  const trackedField = useComplianceField<unknown>(section, field);
  return useMemo(() => trackedField?.isVerified ?? false, [trackedField]);
}

// ============================================================================
// SECTION HOOKS
// ============================================================================

/**
 * Hook to get all personal data fields
 *
 * @returns The SharedPersonalData section
 */
export function usePersonalData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.personal, [data?.personal]);
}

/**
 * Hook to get all contact data fields
 *
 * @returns The SharedContactData section
 */
export function useContactData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.contact, [data?.contact]);
}

/**
 * Hook to get all employment data fields
 *
 * @returns The SharedEmploymentData section
 */
export function useEmploymentData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.employment, [data?.employment]);
}

/**
 * Hook to get all financial data fields
 *
 * @returns The SharedFinancialData section
 */
export function useFinancialData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.financial, [data?.financial]);
}

/**
 * Hook to get all tax data fields
 *
 * @returns The SharedTaxData section
 */
export function useTaxData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.tax, [data?.tax]);
}

/**
 * Hook to get all PEP data fields
 *
 * @returns The SharedPEPData section
 */
export function usePepData() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.pep, [data?.pep]);
}

/**
 * Hook to get beneficial owners list
 *
 * @returns Array of BeneficialOwnerData
 */
export function useBeneficialOwners() {
  const { data } = useComplianceContext();
  return useMemo(() => data?.beneficialOwners ?? [], [data?.beneficialOwners]);
}

// ============================================================================
// LOADING & ERROR STATE HOOKS
// ============================================================================

/**
 * Hook to get loading and error state
 *
 * @returns Object with loading and error state
 */
export function useComplianceLoadingState() {
  const { loading, error } = useComplianceContext();
  return useMemo(() => ({ loading, error }), [loading, error]);
}

/**
 * Hook to get save state
 *
 * @returns Object with isDirty, isSaving, and lastSavedAt
 */
export function useComplianceSaveState() {
  const { isDirty, isSaving, lastSavedAt } = useComplianceContext();
  return useMemo(
    () => ({ isDirty, isSaving, lastSavedAt }),
    [isDirty, isSaving, lastSavedAt]
  );
}

// ============================================================================
// ACTION HOOKS
// ============================================================================

/**
 * Hook to get ingestion actions
 *
 * @returns Object with ingestion functions
 */
export function useComplianceIngestion() {
  const {
    ingestFromDocument,
    ingestFromTranscription,
    ingestFromAPI,
    ingestData,
  } = useComplianceContext();

  return useMemo(
    () => ({
      ingestFromDocument,
      ingestFromTranscription,
      ingestFromAPI,
      ingestData,
    }),
    [ingestFromDocument, ingestFromTranscription, ingestFromAPI, ingestData]
  );
}

/**
 * Hook to get field update and verify actions
 *
 * @returns Object with updateField and verifyField functions
 */
export function useComplianceFieldActions() {
  const { updateField, verifyField } = useComplianceContext();

  return useMemo(
    () => ({ updateField, verifyField }),
    [updateField, verifyField]
  );
}
